import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { requestAssignWearable } from "@/api/InmatesService"
import { Wearable } from "@/components/WearablesModal/types"
import { UNASSIGNED_WEARABLE } from "@/constants/globals"
import { InmateItem } from "@/pages/Settings/Inmates/types"
import { AppDispatch } from "@/store"
import {
   fetchInmates,
   selectInmates,
   updateInmateSensor,
} from "@/store/inmatesSlice"
import { linkWearable, removeWearable } from "@/store/wearablesRealtimeSlice"
import { Sensor } from "@/types/inmate"
import { normalizeMacAddress } from "@/utils/index"

export const useWearablesModal = () => {
   const dispatch = useDispatch<AppDispatch>()

   const inmates = useSelector(selectInmates)

   const handleAssignWearable = useCallback(
      async (inmateId: string, sensor: Sensor | null): Promise<void> => {
         try {
            await requestAssignWearable(inmateId, sensor?.id || "")
            dispatch(updateInmateSensor({ inmateId, sensor }))
         } catch (error) {
            console.error("Error assigning wearable:", error)
            throw error
         }
      },
      [dispatch]
   )

   const handleWearableDisconnect = useCallback(
      async (inmateId: string, wearableId?: string) => {
         await requestAssignWearable(inmateId, "")
         dispatch(fetchInmates())
         if (wearableId && wearableId !== UNASSIGNED_WEARABLE) {
            dispatch(removeWearable(normalizeMacAddress(wearableId)))
         }
      },
      [dispatch]
   )

   const handleModalWearableSave = useCallback(
      async (wearable: Wearable, inmateId: string): Promise<void> => {
         if (!inmateId) return

         const wearableId = wearable.macAddress
            ? normalizeMacAddress(wearable.macAddress)
            : null

         if (wearableId) {
            const inmate =
               inmates.find((inmate) => inmate.id === inmateId) ||
               ({} as InmateItem)
            dispatch(linkWearable({ wearableId, inmate }))
         }

         try {
            await handleAssignWearable(inmateId, wearable)
            dispatch(fetchInmates())
         } catch (error) {
            if (wearableId) {
               dispatch(removeWearable(wearableId))
            }
            console.error("Error saving wearable:", error)
            throw error
         }
      },
      [dispatch, inmates, handleAssignWearable]
   )

   const handleSwapWearable = useCallback(
      async (inmateId: string, sensor: Sensor): Promise<void> => {
         try {
            await requestAssignWearable(inmateId, sensor.id)
            dispatch(fetchInmates())
            const prev = inmates.find((i) => i.id === inmateId)
            if (prev?.sensor?.macAddress) {
               dispatch(
                  removeWearable(normalizeMacAddress(prev.sensor.macAddress))
               )
            }
         } catch (error) {
            console.error("Error swapping wearable:", error)
            throw error
         }
      },
      [dispatch, inmates]
   )

   const handleModalScannedWearableSave = useCallback(
      async (inmateId: string, scannedSensor: Sensor | null): Promise<void> => {
         if (!scannedSensor) return

         const wearableId = scannedSensor.macAddress
            ? normalizeMacAddress(scannedSensor.macAddress)
            : null

         if (wearableId) {
            const inmate =
               inmates.find((inmate) => inmate.id === inmateId) ||
               ({} as InmateItem)
            dispatch(linkWearable({ wearableId, inmate }))
         }

         try {
            await handleAssignWearable(inmateId, scannedSensor)
            dispatch(fetchInmates())
         } catch (error) {
            if (wearableId) {
               dispatch(removeWearable(wearableId))
            }
            console.error("Error saving scanned wearable:", error)
            throw error
         }
      },
      [dispatch, inmates, handleAssignWearable]
   )

   return {
      handleAssignWearable,
      handleModalWearableSave,
      handleModalScannedWearableSave,
      handleSwapWearable,
      handleWearableDisconnect,
   }
}
